/**
 * Enhanced Cache service to prevent excessive API calls
 * with local storage persistence
 */
import config from '../config';

// Cache structure: key -> { data, timestamp, ttl }
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time-to-live in milliseconds
}

interface CacheMetrics {
  hits: number;
  misses: number;
  invalidations: number;
  localStorage: {
    hits: number;
    misses: number;
  };
}

// Constants
const CACHE_PREFIX = 'triple_point_cache_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const LONG_TTL = config.cacheDuration || 24 * 60 * 60 * 1000; // 24 hours by default

class Cache {
  private store: Map<string, CacheEntry<any>> = new Map();
  private metrics: CacheMetrics = {
    hits: 0,
    misses: 0,
    invalidations: 0,
    localStorage: {
      hits: 0,
      misses: 0
    }
  };

  /**
   * Get an item from memory cache
   */
  get<T>(key: string): T | null {
    const entry = this.store.get(key);
    
    if (!entry) {
      this.metrics.misses++;
      return null;
    }
    
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      // Entry has expired
      this.store.delete(key);
      this.metrics.invalidations++;
      return null;
    }
    
    this.metrics.hits++;
    return entry.data as T;
  }

  /**
   * Set an item in memory cache
   */
  set<T>(key: string, data: T, ttl: number): void {
    this.store.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Try to get item from local storage
   */
  getFromLocalStorage<T>(key: string): T | null {
    try {
      const cacheKey = `${CACHE_PREFIX}${key}`;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (!cachedData) {
        this.metrics.localStorage.misses++;
        return null;
      }
      
      const { data, timestamp, ttl } = JSON.parse(cachedData);
      const now = Date.now();
      
      if (now - timestamp > ttl) {
        // Expired data
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      // Also put in memory for faster access next time
      this.set(key, data, ttl - (now - timestamp));
      
      this.metrics.localStorage.hits++;
      return data as T;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  }

  /**
   * Save item to local storage
   */
  saveToLocalStorage<T>(key: string, data: T, ttl: number): void {
    try {
      const cacheKey = `${CACHE_PREFIX}${key}`;
      const cacheData = {
        data,
        timestamp: Date.now(),
        ttl
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      // Continue even if localStorage fails - memory cache will still work
    }
  }

  /**
   * Remove an item from all caches
   */
  remove(key: string): void {
    this.store.delete(key);
    try {
      localStorage.removeItem(`${CACHE_PREFIX}${key}`);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  }

  /**
   * Clear all cache items
   */
  clear(): void {
    this.store.clear();
    
    try {
      // Clear only our prefixed items
      Object.keys(localStorage)
        .filter(key => key.startsWith(CACHE_PREFIX))
        .forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }

  /**
   * Get cache size (number of entries)
   */
  size(): number {
    return this.store.size;
  }

  /**
   * Get cache metrics
   */
  getMetrics(): CacheMetrics {
    return { ...this.metrics };
  }

  /**
   * Reset cache metrics
   */
  resetMetrics(): void {
    this.metrics = {
      hits: 0,
      misses: 0,
      invalidations: 0,
      localStorage: {
        hits: 0,
        misses: 0
      }
    };
  }
}

// Create a singleton instance
const cache = new Cache();

/**
 * Get cached data with multi-level cache approach
 * @param key Cache key
 * @param fetchFunc Function to call if cache miss
 * @param options Cache options
 */
export async function getCachedData<T>(
  key: string,
  fetchFunc: () => Promise<T>,
  options: {
    ttl?: number;
    useLocalStorage?: boolean;
    forceRefresh?: boolean;
  } = {}
): Promise<T> {
  const {
    ttl = DEFAULT_TTL,
    useLocalStorage = true,
    forceRefresh = false
  } = options;
  
  // Skip cache if force refresh
  if (forceRefresh) {
    const data = await fetchFunc();
    
    // Still save to cache for future requests
    cache.set(key, data, ttl);
    if (useLocalStorage) {
      cache.saveToLocalStorage(key, data, ttl);
    }
    
    return data;
  }
  
  // Check memory cache first (fastest)
  const memoryData = cache.get<T>(key);
  if (memoryData !== null) {
    return memoryData;
  }
  
  // Try local storage if enabled
  if (useLocalStorage) {
    const localData = cache.getFromLocalStorage<T>(key);
    if (localData !== null) {
      return localData;
    }
  }
  
  // Cache miss on all levels, fetch fresh data
  const data = await fetchFunc();
  
  // Store in all cache levels
  cache.set(key, data, ttl);
  if (useLocalStorage) {
    cache.saveToLocalStorage(key, data, ttl);
  }
  
  return data;
}

/**
 * Store data in cache
 */
export function setCachedData<T>(
  key: string, 
  data: T, 
  ttl: number = DEFAULT_TTL,
  useLocalStorage: boolean = true
): void {
  cache.set(key, data, ttl);
  if (useLocalStorage) {
    cache.saveToLocalStorage(key, data, ttl);
  }
}

/**
 * Clear a specific cache entry
 */
export function clearCacheEntry(key: string): void {
  cache.remove(key);
}

/**
 * Clear all cache entries
 */
export function clearCache(): void {
  cache.clear();
}

/**
 * Get cache statistics
 */
export function getCacheStats() {
  const metrics = cache.getMetrics();
  const size = cache.size();
  
  const totalRequests = metrics.hits + metrics.misses;
  const hitRate = totalRequests > 0 ? (metrics.hits / totalRequests) * 100 : 0;
  
  const localStorageRequests = metrics.localStorage.hits + metrics.localStorage.misses;
  const localStorageHitRate = localStorageRequests > 0 
    ? (metrics.localStorage.hits / localStorageRequests) * 100 
    : 0;
  
  return {
    size,
    hits: metrics.hits,
    misses: metrics.misses,
    invalidations: metrics.invalidations,
    hitRate: `${hitRate.toFixed(2)}%`,
    localStorage: {
      hits: metrics.localStorage.hits,
      misses: metrics.localStorage.misses,
      hitRate: `${localStorageHitRate.toFixed(2)}%`
    }
  };
}

/**
 * Utility wrapper to fetch with cache support
 * @param key Cache key
 * @param fetchFunc Function to call if cache miss
 * @param ttl Time to live in milliseconds
 */
export const withCache = async <T>(
  key: string,
  fetchFunc: () => Promise<T>,
  ttl: number = DEFAULT_TTL
): Promise<T> => {
  return getCachedData<T>(key, fetchFunc, { ttl });
};

export default {
  getCachedData,
  setCachedData,
  clearCacheEntry,
  clearCache,
  getCacheStats,
  DEFAULT_TTL,
  LONG_TTL,
  withCache
}; 